import {
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  linkedSignal,
  ViewChild
} from '@angular/core';
import {Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {PageGridComponent} from 'src/app/shared/components/page-grid/page-grid.component';
import {GridColumn, GridFilter, GridModel} from 'src/app/shared/model/page-grid';
import {CommissionReconciliationResponse, CommissionReconciliationData} from '../../models/commission-reconciliation';
import {CommissionReconciliationGridService} from '../../service/commission-reconciliation-grid.service';
import {ReconciliationUploadModalComponent} from '../reconciliation-upload-modal/reconciliation-upload-modal.component';
import {DEFAULT_PROCESSORS} from '../../constants/commission-reconciliation-filter';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  CommissionReconciliationPopupComponent
} from '../commission-reconciliation-popup/commission-reconciliation-popup.component';
import {of} from 'rxjs';
import {DatePipe} from "@angular/common";
import {GridFilterService} from "../../../shared/service/grid-filter.service";
import {startWith} from "rxjs/operators";
import {TramsDataSourceModel} from '../../models/trams-data-source';
import {TramsDataSourceService} from '../../service/trams-data-source.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  CommissionReconciliationEditRecordComponent
} from '../commission-reconciliation-edit-record/commission-reconciliation-edit-record.component';


@Component({
  selector: 'app-commission-reconciliation',
  templateUrl: './commission-reconciliation.component.html',
  styleUrls: ['./commission-reconciliation.component.scss'],
  standalone: false
})
export class CommissionReconciliationComponent {
  @ViewChild(PageGridComponent) pageGridComponentRef!: PageGridComponent;

  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly datePipe = inject(DatePipe);
  private readonly commissionReconciliationGridService = inject(CommissionReconciliationGridService);
  private readonly gridFilterService = inject(GridFilterService);
  private dataSourceService = inject(TramsDataSourceService);
  private snackBar = inject(MatSnackBar);
  private columnStyles = (item: CommissionReconciliationData) => {
    if (!item.fmsAdxBookingId) return {'background-color': '#FFFFFF'};
    else if (item.fmsAdxBookingId > 0 && item.commissionStatus === "CommissionAdjusted") return {'background-color': '#FAD4D5'};
    return {'background-color': '#D4FAE0'}
  }

  private readonly COMMON_COLUMNS = computed<GridColumn[]>(() => ([
    {
      columnProperty: 'fileName',
      columnHeader: 'File Name',
      sort: true,
      extraWidth: true,
      style: this.columnStyles
    },
    {
      columnProperty: 'clientName',
      columnHeader: 'Client Name',
      sort: true,
      style: this.columnStyles
    },
    {
      columnProperty: 'departureDate',
      columnHeader: 'Departure Date',
      sort: true,
      style: this.columnStyles,
      processData: (date: string) => this.datePipe.transform(date, 'd/M/y') ?? '',
    },
    {
      columnProperty: 'arrivalDate',
      columnHeader: 'Arrival Date',
      sort: true,
      style: this.columnStyles,
      processData: (date: string) => this.datePipe.transform(date, 'd/M/y') ?? '',
    },
    {
      columnProperty: 'vendorName',
      columnHeader: 'Vendor Name',
      sort: true,
      style: this.columnStyles,
    },
    {
      columnProperty: 'confirmationNumber',
      columnHeader: 'Confirmation Number',
      sort: true,
      extraWidth: true,
      whiteSpace: 'pre-wrap',
      style: this.columnStyles,
    },
    {
      columnProperty: 'fmsAdxBookingId',
      columnHeader: 'Fms Booking Number',
      sort: true,
      style: this.columnStyles
    },
    {
      columnProperty: 'adxBookingCommAmt',
      columnHeader: 'In Fms Commission Amount',
      sort: true,
      style: this.columnStyles,
    },
    {
      columnProperty: 'dataSourceId',
      columnHeader: 'Data Source Id',
      sort: false,
      shouldHideColumn: true,
      style: this.columnStyles
    },
    {
      columnProperty: 'dataSourceName',
      columnHeader: 'Data Source Name',
      sort: true,
      style: this.columnStyles
    },
  ]));

  private readonly ONYX_COLUMNS = computed(() => {
    const COMMON_COLUMNS = this.COMMON_COLUMNS();
    const array = [...COMMON_COLUMNS];
    array.splice(this.COMMON_COLUMNS().length - 1, 0, ...[
      {
        columnProperty: 'commissionAmount',
        columnHeader: 'On File Commission Amount',
        sort: true,
        style: this.columnStyles,
      },
      {
        columnProperty: 'grossCommissionDue',
        columnHeader: 'Commission Due',
        sort: true,
        style: this.columnStyles,
      },
    ]);
    return array;
  });

  private readonly TACS_COLUMNS = computed(() => {
    const COMMON_COLUMNS = this.COMMON_COLUMNS();
    const array = [...COMMON_COLUMNS];
    array.splice(this.COMMON_COLUMNS().length - 1, 0, ...[
      {
        columnProperty: 'commissionAmount',
        columnHeader: 'On File Commission Amount',
        sort: true,
        style: this.columnStyles,
      },
      {
        columnProperty: 'reportRevenue',
        columnHeader: 'Commissionable Rev.',
        sort: true,
        style: this.columnStyles,
      },
    ]);
    return array;
  });

  private readonly PAYMODE_COLUMNS = computed(() => {
    const COMMON_COLUMNS = this.COMMON_COLUMNS();
    const array = [...COMMON_COLUMNS];
    array.splice(this.COMMON_COLUMNS().length - 1, 0, ...[
      {
        columnProperty: 'commissionAmount',
        columnHeader: 'On File Commission Amount',
        sort: true,
        style: this.columnStyles,
      },
    ]);
    return array;
  });

  private readonly gridColumns = computed(() => {
    const processorId = this.commissionReconciliationGridService.processorId();
    if (!processorId || processorId === 4) return this.ONYX_COLUMNS();
    if (processorId === 5) return this.TACS_COLUMNS();
    return this.PAYMODE_COLUMNS();
  });

  private readonly gridFilters = linkedSignal<GridFilter[]>(() => {
    const appliedFilters = this.gridFilterService.filters();
    const processorId = appliedFilters["processorId"];
    const filters = [
      {
        filterProperty: 'processorId',
        filterDisplayName: 'Processor',
        filterType: 'DROPDOWN',
        filterResetValue: 4,
        config: {
          dropdownOptions: of(DEFAULT_PROCESSORS),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: { displayName: string }) => item.displayName
        },
        validators: [Validators.required]
      },
      {
        filterProperty: 'fileName',
        filterDisplayName: 'File Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'OnyxBatchHeader',
          autocompleteColumnName: 'FileName',
          autocompleteEmitText: true,
        }
      },
      {
        filterProperty: 'arrivalDateFrom',
        filterDisplayName: 'Arrival Date From',
        filterType: 'DATE',
      },
      {
        filterProperty: 'arrivalDateTo',
        filterDisplayName: 'Arrival Date To',
        filterType: 'DATE',
      },
      {
        filterProperty: 'departureDateFrom',
        filterDisplayName: 'Departure Date From',
        filterType: 'DATE',
      },
      {
        filterProperty: 'departureDateTo',
        filterDisplayName: 'Departure Date To',
        filterType: 'DATE',
      },
      {
        filterProperty: 'hotelConfirmationNumber',
        filterDisplayName: 'Confirmation Number',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'OnyxReportedCommission',
          autocompleteColumnName: 'HotelConfirmationNumber',
          autocompleteEmitText: true,
        }
      },
      {
        filterProperty: 'adxTripReference',
        filterDisplayName: 'Invoice Record Locator',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Invoice',
          autocompleteColumnName: 'RecordLocator',
          autocompleteEmitText: true,
        }
      },
      {
        filterProperty: 'dataSourceId',
        filterDisplayName: 'Data Source',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.dataSourceService.getTramsDataSourceList(),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: TramsDataSourceModel) => item.displayName
        },
      },
    ] as GridFilter[];
    if (!processorId) return filters;
    const fileNameFilter = filters.find(filter => filter.filterProperty === 'fileName')!;
    const hotelConfirmationNumberFilter = filters.find(filter => filter.filterProperty === 'hotelConfirmationNumber')!;

    const getFileNameFilter = () => {
      if (processorId === 4) return ['OnyxBatchHeader', 'FileName'];
      if (processorId === 5) return ['Tacs', 'FileName'];
      return ['Paymode', 'FileName'];
    };
    const getHotelConfirmationNumberFilter = () => {
      if (processorId === 4) return ['OnyxReportedCommission', 'HotelConfirmationNumber'];
      if (processorId === 5) return ['Tacs', 'Confirmation'];
      return ['Paymode', 'Reservation'];
    };

    const [fileNameTableName, fileNameColumnName] = getFileNameFilter();
    fileNameFilter.config = {
      autocompleteEmitText: true,
      autocompleteTableName: fileNameTableName,
      autocompleteColumnName: fileNameColumnName,
    };
    const [hotelConfirmationNumberTableName, hotelConfirmationNumberColumnName] = getHotelConfirmationNumberFilter();
    hotelConfirmationNumberFilter.config = {
      autocompleteEmitText: true,
      autocompleteTableName: hotelConfirmationNumberTableName,
      autocompleteColumnName: hotelConfirmationNumberColumnName,
    };
    return filters;
  });

  protected readonly commissionReconciliationGrid = computed<GridModel<CommissionReconciliationResponse>>(() => ({
    gridDataTestId: 'commission-reconciliation-list',
    gridTitle: 'Commission Reconciliation',
    replayButton: true,
    defaultSort: {active: 'id', direction: 'desc'},
    showActionColumn: true,
    pdfButton: true,
    eventButton: {text: 'Upload File', icon: 'upload_file'},
    secondEventButton: {text: 'Apply Update', icon: ''},
    columns: this.gridColumns(),
    filters: this.gridFilters(),
    gridGetService: this.commissionReconciliationGridService
  }));

  @HostListener('document:copy', ['$event'])
  public onCopy(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;
    const target = event.target as HTMLSpanElement;
    const value = target.textContent;
    if (!value) return;
    clipboardData.setData("text/plain", value.trim());
    event.preventDefault();
  }

  protected applyUpdate() {

    const checkedItems = this.pageGridComponentRef.items.filter(
      (element) => (element as CommissionReconciliationData).isChecked
    ) as CommissionReconciliationData[];

    const checkedCount = checkedItems.length;

    const allSameDataSource =
      checkedItems.length > 0 &&
      checkedItems.every(
        (item) => item.dataSourceId === checkedItems[0].dataSourceId
      );

    if (!checkedCount) {
      this.snackBar.open('No items are selected to update!', 'Dismiss', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    } else if (!allSameDataSource) {
      this.snackBar.open('Select only items from the same Data Source!', 'Dismiss', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    } else {
      this.dialog.open(
        CommissionReconciliationPopupComponent,
        {
          data: {checkedCount: checkedCount, checkedItems: checkedItems, dataSourceId: checkedItems[0]?.dataSourceId},
        }).afterClosed().subscribe((confirmation: boolean) => {
        if (confirmation) {
          this.pageGridComponentRef?.getItems();
        }
      });
    }
  }

  protected uploadFileDialog() {
    const dialog = this.dialog.open(ReconciliationUploadModalComponent);
    dialog.componentInstance.ccProcessors.set(DEFAULT_PROCESSORS);
    dialog.componentInstance.acceptFiles.set(['.zip']);
    dialog.componentInstance.handleUpload.set(true);

    const processorCtrl = dialog.componentInstance.reconciliationForm.controls['ccProcessorId'];
    processorCtrl.valueChanges
      .pipe(
        startWith(processorCtrl.value),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value?: number) => {
        if (value === 5) return dialog.componentInstance.acceptFiles.set(['.txt']);
        if (value === 6) return dialog.componentInstance.acceptFiles.set(['.gcp']);
        dialog.componentInstance.acceptFiles.set(['.zip']);
      });

    dialog
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (sentFile?: { file: { file: File }; ccProcessorId: number }) => {
          if (!sentFile || !sentFile.file) return;
          this.uploadFile(sentFile.ccProcessorId, sentFile.file.file);
        }
      );
  }

  private uploadFile(processorId: number, file: File) {
    this.commissionReconciliationGridService
      .uploadFile(processorId, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.pageGridComponentRef.getItems();
        },
      });
  }

  openEditCommissionReconciliationModal(element: any): void {
    const dialogRef = this.dialog.open(CommissionReconciliationEditRecordComponent, {
      data: {
        ccProcessorId: element.processorId,
        processorRecordId: element.id
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.mapped) {
        this.pageGridComponentRef?.getItems();
      }
    });
  }
}
